/**
 * The audio meter's maths (0.96.0), pure so it can be tested without a browser: sample peaks per channel with a
 * peak hold and a sticky over, and EBU R128 loudness after ITU-R BS.1770-4.
 *
 * Loudness:
 *  - each channel is K-weighted (the BS.1770 pre-filter, a high shelf, then the RLB high-pass), with coefficients
 *    derived for the actual sample rate (the derivation libebur128 uses; at 48 kHz it gives the table in BS.1770);
 *  - mean squares are kept per 100 ms sub-block; a 400 ms gating block is four of them, so blocks overlap by 75 %;
 *  - momentary loudness is the last 400 ms, short-term the last 3 s (EBU Tech 3341), both ungated;
 *  - integrated loudness gates the 400 ms blocks: absolute gate −70 LUFS, then a relative gate 10 LU below the
 *    loudness of the blocks above the absolute gate;
 *  - L = −0.691 + 10·log10(Σ Gᵢ·zᵢ) with G = 1 for left and right (the stereo channels are summed, not averaged).
 *
 * Calibration (the convention used here): a 1 kHz sine with a sample peak of −18 dBFS on both channels of a
 * stereo signal reads −18.0 LUFS (the K-filter's +0.69 dB at 1 kHz cancels the −0.691 offset, and the two
 * channels' sum adds the +3.01 dB that a sine's mean square, 3.01 dB below its peak, loses). The same tone on one
 * channel only reads −21.0 LUFS, and some meters that average the channels show −21 for the stereo tone too.
 */

export interface Biquad { b: [number, number, number]; a: [number, number, number] }

/** The two K-weighting stages for a sample rate (a[0] is 1). */
export function kWeighting(sampleRate: number): { shelf: Biquad; highpass: Biquad } {
  // Stage 1: high shelf, about +4 dB above ~1.5 kHz (the head's acoustic effect).
  let f0 = 1681.974450955533;
  const G = 3.999843853973347;
  let Q = 0.7071752369554196;
  let K = Math.tan((Math.PI * f0) / sampleRate);
  const Vh = Math.pow(10, G / 20);
  const Vb = Math.pow(Vh, 0.4996667741545416);
  let a0 = 1 + K / Q + K * K;
  const shelf: Biquad = {
    b: [(Vh + (Vb * K) / Q + K * K) / a0, (2 * (K * K - Vh)) / a0, (Vh - (Vb * K) / Q + K * K) / a0],
    a: [1, (2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0],
  };
  // Stage 2: the RLB high-pass at ~38 Hz.
  f0 = 38.13547087602444;
  Q = 0.5003270373238773;
  K = Math.tan((Math.PI * f0) / sampleRate);
  a0 = 1 + K / Q + K * K;
  const highpass: Biquad = { b: [1, -2, 1], a: [1, (2 * (K * K - 1)) / a0, (1 - K / Q + K * K) / a0] };
  return { shelf, highpass };
}

/** A biquad with state (direct form I), run over a stream of samples. */
class BiquadState {
  private x1 = 0; private x2 = 0; private y1 = 0; private y2 = 0;
  private readonly f: Biquad;
  constructor(f: Biquad) { this.f = f; }
  step(x: number): number {
    const { b, a } = this.f;
    const y = b[0] * x + b[1] * this.x1 + b[2] * this.x2 - a[1] * this.y1 - a[2] * this.y2;
    this.x2 = this.x1; this.x1 = x; this.y2 = this.y1; this.y1 = y;
    return y;
  }
  reset(): void { this.x1 = this.x2 = this.y1 = this.y2 = 0; }
}

export const ABSOLUTE_GATE_LUFS = -70;
export const RELATIVE_GATE_LU = -10;
const SUBBLOCKS_MOMENTARY = 4; // 400 ms
const SUBBLOCKS_SHORT_TERM = 30; // 3 s

/** Loudness of a mean-square power sum: −0.691 + 10·log10(z), or −Infinity for silence. */
export const lufsOf = (power: number): number => (power > 0 ? -0.691 + 10 * Math.log10(power) : -Infinity);

/** EBU R128 momentary, short-term and integrated loudness of a stream of (up to stereo) samples. */
export class LoudnessMeter {
  readonly sampleRate: number;
  readonly channels: number;
  private readonly filters: Array<[BiquadState, BiquadState]>;
  private readonly subLen: number;
  /** Σ over channels of the sub-block's mean square, newest last (only as many as short-term needs). */
  private subs: number[] = [];
  private acc = 0;
  private accN = 0;
  /** Power of every 400 ms gating block since the reset, for the integrated loudness. */
  private blocks: number[] = [];

  constructor(sampleRate: number, channels = 2) {
    this.sampleRate = sampleRate;
    this.channels = channels;
    const k = kWeighting(sampleRate);
    this.filters = Array.from({ length: channels }, () => [new BiquadState(k.shelf), new BiquadState(k.highpass)]);
    this.subLen = Math.round(sampleRate / 10);
  }

  /** New samples since the last push, one array per channel, all the same length. */
  push(chans: ArrayLike<number>[]): void {
    const n = chans[0]?.length ?? 0;
    for (let i = 0; i < n; i++) {
      let sum = 0;
      for (let c = 0; c < this.channels; c++) {
        const x = chans[c]?.[i] ?? 0;
        const f = this.filters[c]!;
        const y = f[1].step(f[0].step(x));
        sum += y * y;
      }
      this.acc += sum;
      if (++this.accN === this.subLen) this.closeSub();
    }
  }

  private closeSub(): void {
    this.subs.push(this.acc / this.subLen);
    this.acc = 0;
    this.accN = 0;
    if (this.subs.length > SUBBLOCKS_SHORT_TERM) this.subs.shift();
    if (this.subs.length >= SUBBLOCKS_MOMENTARY) this.blocks.push(this.mean(SUBBLOCKS_MOMENTARY));
  }

  private mean(count: number): number {
    const k = Math.min(count, this.subs.length);
    if (!k) return 0;
    let s = 0;
    for (let i = this.subs.length - k; i < this.subs.length; i++) s += this.subs[i]!;
    return s / k;
  }

  /** The last 400 ms (LUFS); −Infinity before the first 400 ms. */
  momentary(): number { return this.subs.length >= SUBBLOCKS_MOMENTARY ? lufsOf(this.mean(SUBBLOCKS_MOMENTARY)) : -Infinity; }

  /** The last 3 s (LUFS); over what there is during the first 3 s. */
  shortTerm(): number { return this.subs.length >= SUBBLOCKS_MOMENTARY ? lufsOf(this.mean(SUBBLOCKS_SHORT_TERM)) : -Infinity; }

  /** Gated integrated loudness since the reset (LUFS); −Infinity when nothing is above the gates. */
  integrated(): number { return integratedLoudness(this.blocks); }

  reset(): void {
    this.subs = [];
    this.blocks = [];
    this.acc = 0;
    this.accN = 0;
    for (const [a, b] of this.filters) { a.reset(); b.reset(); }
  }
}

/** BS.1770-4 gating over 400 ms block powers: absolute gate, then relative gate. */
export function integratedLoudness(blocks: number[]): number {
  const absPower = Math.pow(10, (ABSOLUTE_GATE_LUFS + 0.691) / 10);
  const loud = blocks.filter((p) => p > absPower);
  if (!loud.length) return -Infinity;
  const relGate = lufsOf(loud.reduce((s, p) => s + p, 0) / loud.length) + RELATIVE_GATE_LU;
  const relPower = Math.pow(10, (relGate + 0.691) / 10);
  const gated = loud.filter((p) => p > relPower);
  if (!gated.length) return -Infinity;
  return lufsOf(gated.reduce((s, p) => s + p, 0) / gated.length);
}

/** Sample peak in dBFS (−Infinity for silence). */
export const dbfs = (amplitude: number): number => (amplitude > 0 ? 20 * Math.log10(amplitude) : -Infinity);

export const PEAK_HOLD_MS = 2000;
/** The bar falls back this fast after a peak (dB per second; IEC 60268-18 style). */
export const PEAK_FALLBACK_DB_PER_S = 20;
/** At or above this sample value a channel is "over" (0 dBFS), until reset. */
export const OVER_AMPLITUDE = 1;

/** One channel's peak bar: the level (falling back slowly), the held peak (2 s) and a sticky over. */
export class PeakMeter {
  level = -Infinity;
  hold = -Infinity;
  over = false;
  private holdAt = 0;
  private lastAt: number | null = null;

  /** New samples at time `now` (ms). */
  push(samples: ArrayLike<number>, now: number): void {
    let peak = 0;
    for (let i = 0; i < samples.length; i++) {
      const v = Math.abs(samples[i]!);
      if (v > peak) peak = v;
    }
    if (peak >= OVER_AMPLITUDE) this.over = true;
    const db = dbfs(peak);
    const dt = this.lastAt == null ? 0 : Math.max(0, now - this.lastAt) / 1000;
    this.lastAt = now;
    const fallen = this.level - PEAK_FALLBACK_DB_PER_S * dt;
    this.level = Math.max(db, fallen);
    if (db >= this.hold || now - this.holdAt > PEAK_HOLD_MS) { this.hold = Math.max(db, this.level); this.holdAt = now; }
  }

  reset(): void {
    this.level = -Infinity;
    this.hold = -Infinity;
    this.over = false;
    this.holdAt = 0;
    this.lastAt = null;
  }
}

/** Position of a level on the meter's scale (−60…0 dBFS) as 0…1. */
export const meterPosition = (db: number, floor = -60): number => (Number.isFinite(db) ? Math.max(0, Math.min(1, (db - floor) / -floor)) : 0);

/* ---------------------------------------------------------------- the Meters pane's loudness bars (1.7.4) */

/** The loudness targets the Meters pane offers (usePref editor.loudnessTarget). */
export type LoudnessTarget = 'ebu' | 'streaming' | 'atsc';
export const LOUDNESS_TARGETS: Record<LoudnessTarget, { lufs: number; label: string; name: string }> = {
  ebu: { lufs: -23, label: 'EBU R128 −23', name: 'EBU R128' },
  streaming: { lufs: -14, label: 'Streaming −14', name: 'Streaming' },
  atsc: { lufs: -24, label: 'ATSC A/85 −24', name: 'ATSC A/85' },
};
/** A saved target, or EBU R128 for anything else. */
export const loudnessTargetOf = (v: unknown): LoudnessTarget => (v === 'streaming' || v === 'atsc' ? v : 'ebu');

/** The loudness bars' scale: −40 LUFS at the bottom, 0 at the top. */
export const LUFS_FLOOR = -40;
/** A loudness on the bars' scale (0 = bottom, 1 = top; −∞ is 0). */
export const lufsPosition = (lufs: number): number => meterPosition(lufs, LUFS_FLOOR);
/** Whether a reading is above the target (the bar turns amber); −∞ never is. */
export const aboveTarget = (lufs: number, target: LoudnessTarget): boolean => Number.isFinite(lufs) && lufs > LOUDNESS_TARGETS[target].lufs;
